import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { detectPlatform, getStoreUrlForPlatform } from '@/utils/appLinks';
import { trackEvent } from '@/lib/analytics';
import {
  getStoredConsent,
  CONSENT_REOPEN_EVENT,
  CONSENT_SAVED_EVENT,
} from '@/lib/cookieConsent';

const DISMISS_KEY = 'joyn_app_bar_dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

// Classes aplicadas no <body> enquanto a barra está visível, pra ela não
// cobrir o fim da página (rodapé incluso). md:pb-0 porque a barra some do
// md pra cima.
const BODY_PADDING_CLASSES = ['pb-[calc(4.5rem+env(safe-area-inset-bottom))]', 'md:pb-0'];

function wasDismissedRecently() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    return Boolean(ts) && Date.now() - ts < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function saveDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // storage bloqueado — fecha só nesta visita
  }
}

// Barra fixa "baixe o app" — só no celular (iOS/Android) e só onde for
// montada (hoje, página do evento). Espera o banner de cookies sair da tela
// pra não empilhar duas barras no rodapé.
export function DownloadAppStickyBar() {
  const [platform] = useState(detectPlatform);
  const [dismissed, setDismissed] = useState(wasDismissedRecently);
  const [cookieBannerOpen, setCookieBannerOpen] = useState(() => !getStoredConsent());

  useEffect(() => {
    const handleOpen = () => setCookieBannerOpen(true);
    const handleSaved = () => setCookieBannerOpen(false);
    window.addEventListener(CONSENT_REOPEN_EVENT, handleOpen);
    window.addEventListener(CONSENT_SAVED_EVENT, handleSaved);
    return () => {
      window.removeEventListener(CONSENT_REOPEN_EVENT, handleOpen);
      window.removeEventListener(CONSENT_SAVED_EVENT, handleSaved);
    };
  }, []);

  const storeUrl = getStoreUrlForPlatform(platform);
  const visible = Boolean(storeUrl) && !dismissed && !cookieBannerOpen;

  useEffect(() => {
    if (!visible) return;
    document.body.classList.add(...BODY_PADDING_CLASSES);
    return () => document.body.classList.remove(...BODY_PADDING_CLASSES);
  }, [visible]);

  if (!visible) return null;

  const handleDismiss = () => {
    saveDismissed();
    setDismissed(true);
  };

  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
      role="region"
      aria-label="Baixar o app Joyn"
      data-testid="download-app-sticky-bar"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <img src="/logo_eventfy.png" alt="" aria-hidden="true" className="w-9 h-9 rounded-xl shrink-0" />
        <p className="flex-1 text-sm font-semibold leading-tight">Veja mais eventos no app Joyn</p>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('click_download_app', {
            platform,
            store: platform === 'ios' ? 'app_store' : 'google_play',
            placement: 'sticky_bar',
          })}
          className="shrink-0 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-semibold transition-colors"
        >
          Baixar
        </a>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 -mr-1 p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-colors"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
